
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveApiBase } from '@/utils/apiBase';
import {
  Send,
  Paperclip,
  Search,
  Check,
  CheckCheck,
  Smile,
  MessageCircle,
  Inbox,
  UserCheck,
  Clock,
  Archive,
  XCircle,
  AlertCircle,
  CheckSquare,
  ArrowLeft,
  Tag,
  UserPlus,
  Mic,
  MoreVertical,
  ChevronDown
} from "lucide-react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import { WhatsAppStatus } from "@/types";
import { fetchWhatsAppStatus as fetchWhatsAppStatusWithFallback } from "@/utils/whatsapp";

interface SupportPageProps {
  darkMode: boolean;
  onNavigateToBroker: (brokerId: number) => void;
  onPendingCountChange?: (count: number) => void;
  onNewMessage?: () => void;
  agentName?: string;
  role?: "admin" | "staff" | "client";
   userId?: number;
  whatsappApiBase?: string;
}

type ApiAtendimento = {
  id: number;
  contato_whatsapp: string;
  contato_nome?: string | null;
  contato_foto_url?: string | null;
  status: string;
  created_at?: string;
  updated_at?: string;
  primeira_mensagem_em?: string;
  primeira_resposta_em?: string;
  categoria_id?: number | null;
  produto_id?: number | null;
  produto_nome?: string | null;
  imobiliaria_id?: number | null;
  imobiliaria_nome?: string | null;
  atendente_id?: number | null;
  atendente_nome?: string | null;
};

type ApiMensagem = {
  id: number;
  atendimento_id: number;
  direction: "IN" | "OUT" | "SYS";
  whatsapp_msg_id?: string | null;
  body: string;
  media_type: string;
  media_url?: string | null;
  sender_name?: string | null;
  sent_at?: string | null;
  quoted_msg_id?: string | null;
  reaction?: string | null;
  apagada?: boolean;
  apagada_em?: string | null;
  apagada_por?: number | null;
};

type ApiCategoria = { id: number; nome: string; slug: string };

type TicketStatus = "pending" | "open" | "closed";

type ReactionTag = {
  emoji: string;
  count: number;
  actors: string[];
  sources: ("desk" | "whatsapp")[];
};

interface Message {
  id: number;
  text: string;
  sender: "me" | "customer" | "system";
  time: string;
  status: "sent" | "delivered" | "read";
  mediaType?: string;
  mediaUrl?: string | null;
  replyToId?: string | null;
  whatsappMsgId?: string | null;
  reaction?: string | null;
  reactionSource?: "desk" | "whatsapp";
  reactionActorName?: string | null;
  reactions?: ReactionTag[];
  deleted?: boolean;
  deletedAt?: string | null;
  deletedBy?: number | null;
}

interface Ticket {
  rawId: number;
  id: string;
  customerName: string;
  customerAvatar: string;
  avatarUrl?: string;
  platform: "whatsapp";
  status: TicketStatus;
  waitingSince: string;
  lastUpdate: string;
  lastMessageAt?: string | null;
  messages: Message[];
  unreadCount: number;
  phone: string;
  brokerId?: number | null;
  produtoId?: number | null;
  produtoNome?: string | null;
  imobiliariaId?: number | null;
  imobiliariaNome?: string | null;
  agentId?: number | null;
  agentName?: string | null;
}

interface ProductOption {
  id: number;
  name: string;
}

interface ImobiliariaOption {
  id: number;
  name: string;
}

const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');
const WHATSAPP_API = resolveApiBase(import.meta.env.VITE_WHATSAPP_API, '/whatsapp', 'http://localhost:3030');
const QUICK_EMOJIS = ["\u{1F44D}", "\u{2764}\u{FE0F}", "\u{1F602}", "\u{1F62E}", "\u{1F622}", "\u{1F64F}", "\u{1F44F}", "\u{1F525}"];
const ALL_EMOJIS = [
  ...QUICK_EMOJIS,
  "\u{1F60D}", "\u{1F621}", "\u{1F914}", "\u{1F605}", "\u{1F60E}", "\u{1F92F}", "\u{1F973}", "\u{1F634}",
  "\u{1F4AF}", "\u{1F389}", "\u{1F4AA}", "\u{1F64C}", "\u{1F607}", "\u{1F61C}", "\u{1F91D}", "\u{2705}",
  "\u{1F440}", "\u{1F494}", "\u{1F624}", "\u{1F62D}", "\u{1F606}", "\u{1F648}", "\u{1F917}", "\u{2728}"
];
const SOUND_BASE = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
const INCOMING_SOUND_SRC = `${SOUND_BASE}/sounds/atendimento-in.ogg`;
const INCOMING_SOUND_FALLBACK = `${SOUND_BASE}/sounds/atendimento-in.mp3`;
const MESSAGES_PAGE_SIZE = 120;

const mapStatus = (status?: string): TicketStatus => {
  switch ((status || "").toLowerCase()) {
    case "aguardando_atendimento":
      return "pending";
    case "em_atendimento":
    case "aguardando_resposta_corretor":
      return "open";
    case "encerrado":
    case "descartado":
      return "closed";
    default:
      return "pending";
  }
};

const normalizeMediaUrl = (raw?: string | null, token?: string): string | undefined => {
  if (!raw) return undefined;
  const base = CORE_API.replace(/\/$/, "");
  const hasProtocol = /^https?:\/\//i.test(raw);
  const cleanedPath = raw.replace(/^\/+/, "");
  let url = hasProtocol ? raw : `${base}/${cleanedPath}`;
  if (hasProtocol) {
    try {
      const parsed = new URL(raw);
      if (parsed.hostname === "localhost" && parsed.pathname.startsWith("/media/")) {
        url = `${base}${parsed.pathname}${parsed.search}`;
      }
    } catch {
      // ignore
    }
  }
  if (!token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(token)}`;
};

const upsertReaction = (
  current: ReactionTag[] | undefined,
  emoji: string,
  source?: "desk" | "whatsapp",
  actorName?: string | null
): ReactionTag[] => {
  if (!emoji) return current || [];
  const list = current ? [...current] : [];
  const idx = list.findIndex(r => r.emoji === emoji);
  const normActor = actorName || null;
  if (idx >= 0) {
    const item = { ...list[idx] };
    const hasActor = normActor ? item.actors.includes(normActor) : false;
    const hasSource = source ? item.sources.includes(source) : false;
    if (normActor && !hasActor) {
      item.actors = [...item.actors, normActor];
    }
    if (source && !hasSource) {
      item.sources = [...item.sources, source];
    }
    const shouldIncrement = normActor ? !hasActor : true;
    item.count = Math.max(1, item.count + (shouldIncrement ? 1 : 0));
    list[idx] = item;
  } else {
    list.push({
      emoji,
      count: 1,
      actors: normActor ? [normActor] : [],
      sources: source ? [source] : []
    });
  }
  return list;
};

const formatTime = (dateStr?: string | null) => {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const buildTicket = (a: ApiAtendimento): Ticket => {
  const status = mapStatus(a.status);
  const avatar = (a.contato_nome || a.contato_whatsapp || "?").slice(0, 2).toUpperCase();
  const agentId = (a as any).atendente_id ?? (a as any).id_atendente ?? null;
  const agentName = (a as any).atendente_nome ?? (a as any).id_atendente_nome ?? a.atendente_nome ?? null;
  return {
    rawId: a.id,
    id: `#${a.id}`,
    customerName: a.contato_nome || a.contato_whatsapp || "Contato",
    customerAvatar: avatar,
    avatarUrl: a.contato_foto_url || undefined,
    platform: "whatsapp",
    status,
    waitingSince: "",
    lastUpdate: formatTime(a.updated_at || a.created_at),
    lastMessageAt: a.updated_at || a.created_at || null,
    messages: [],
    unreadCount: 0,
    phone: a.contato_whatsapp,
    brokerId: (a as any).id_corretor ?? (a as any).corretor_id ?? null,
    produtoId: a.produto_id || null,
    produtoNome: a.produto_nome || null,
    imobiliariaId: a.imobiliaria_id || null,
    imobiliariaNome: a.imobiliaria_nome || null,
    agentId: agentId || null,
    agentName: agentName || null
  };
};

const shouldIgnoreApiMensagem = (m: ApiMensagem) => {
  const body = (m.body || "").trim();
  const mediaType = String(m.media_type || "").toUpperCase();
  const hasMedia = !!m.media_url;
  return !body && !hasMedia && mediaType === "OTHER";
};

const mapMensagem = (m: ApiMensagem): Message => ({
  id: m.id,
  text: m.body,
  sender: m.direction === "OUT" ? "me" : m.direction === "SYS" ? "system" : "customer",
  time: formatTime(m.sent_at || undefined),
  status: "read",
  mediaType: m.media_type,
  mediaUrl: m.media_url || undefined,
  whatsappMsgId: m.whatsapp_msg_id || undefined,
  replyToId: m.quoted_msg_id || null,
  reaction: m.reaction || undefined,
  reactionSource: undefined,
  reactionActorName: null,
  reactions: m.reaction ? [{ emoji: m.reaction, count: 1, actors: [], sources: [] }] : [],
  deleted: !!m.apagada,
  deletedAt: m.apagada_em || undefined,
  deletedBy: m.apagada_por || undefined
});
const SupportPage: React.FC<SupportPageProps> = ({ darkMode, onNavigateToBroker, onPendingCountChange, onNewMessage, agentName, role, userId, whatsappApiBase }) => {
  const [activeTab, setActiveTab] = useState<"pending" | "mine" | "all">("pending");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [inputText, setInputText] = useState("");
  const [categories, setCategories] = useState<ApiCategoria[]>([]);
  const [closeData, setCloseData] = useState({ reason: "", category: "", productId: "" });
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);

  const openBrokerProfileInNewTab = (brokerId?: number | null) => {
    if (!brokerId) return;
    window.open(`/corretores/${brokerId}`, "_blank", "noopener,noreferrer");
  };
  const [registerData, setRegisterData] = useState({ name: "", email: "", productId: "", imobiliariaId: "", brokerType: "imobiliaria" as "imobiliaria" | "autonomo" | "cliente" });
  const [transferData, setTransferData] = useState({ atendenteId: "" });
  const [transferUsers, setTransferUsers] = useState<{ id: number; nome: string }[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [imobiliarias, setImobiliarias] = useState<ImobiliariaOption[]>([]);
  const [imobiliariaSearch, setImobiliariaSearch] = useState("");
  const [isImobiliariaDropdownOpen, setIsImobiliariaDropdownOpen] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [showActionsMobile, setShowActionsMobile] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [previewMedia, setPreviewMedia] = useState<{ url: string; type: string; text?: string } | null>(null);
  const [previewZoom, setPreviewZoom] = useState<number>(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState<{ file: File; previewUrl: string; mime: string; name: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const [ticketSearch, setTicketSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [menuMessageId, setMenuMessageId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState<{ top: number; left: number; right: number; bottom: number; width: number; height: number } | null>(null);
  const [deletingMessageId, setDeletingMessageId] = useState<number | null>(null);
  const [reactionsExpandedFor, setReactionsExpandedFor] = useState<number | null>(null);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState<number | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(false);
  const replyBarRef = useRef<HTMLDivElement>(null);
  const incomingSoundRef = useRef<HTMLAudioElement | null>(null);
  const incomingSoundUrlRef = useRef<string | null>(null);
  const incomingSoundUnlockedRef = useRef<boolean>(false);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const suppressAutoScrollRef = useRef<boolean>(false);
  const imobiliariaDropdownRef = useRef<HTMLDivElement | null>(null);

  const pendingTickets = useMemo(() => tickets.filter(t => t.status === "pending"), [tickets]);
  const myTickets = useMemo(() => {
    if (!userId) return [];
    return tickets.filter(t => t.status !== "closed" && t.agentId === userId);
  }, [tickets, userId]);
  const allActiveTickets = useMemo(() => tickets.filter(t => t.status !== "closed"), [tickets]);
  const isAdmin = role === "admin";
  const filteredImobiliarias = useMemo(() => {
    const term = imobiliariaSearch.trim().toLowerCase();
    if (!term) return imobiliarias;
    return imobiliarias.filter(item => item.name.toLowerCase().includes(term));
  }, [imobiliarias, imobiliariaSearch]);

  // Evita aba "Geral" para staff/client
  useEffect(() => {
    if (!isAdmin && activeTab === "all") {
      setActiveTab("pending");
    }
  }, [activeTab, isAdmin]);

  useEffect(() => {
    onPendingCountChange?.(pendingTickets.length);
  }, [pendingTickets, onPendingCountChange]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(ticketSearch.trim());
    }, 250);
    return () => clearTimeout(t);
  }, [ticketSearch]);

  useEffect(() => {
    const closeMenu = () => {
      setMenuMessageId(null);
      setMenuPosition(null);
      setReactionsExpandedFor(null);
      setMenuAnchorRect(null);
    };
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (!showActionsMobile) return;
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(ev.target as Node)) {
        setShowActionsMobile(false);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [showActionsMobile]);

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (imobiliariaDropdownRef.current && !imobiliariaDropdownRef.current.contains(ev.target as Node)) {
        setIsImobiliariaDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEsc = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && isCloseModalOpen) {
        setIsCloseModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isCloseModalOpen]);

  useEffect(() => {
    if (!isRegisterModalOpen) {
      setImobiliariaSearch("");
      setIsImobiliariaDropdownOpen(false);
    }
  }, [isRegisterModalOpen]);

  useEffect(() => {
    const setupAudio = () => {
      const audio = new Audio();
      audio.preload = "auto";
      audio.volume = 0.6;
      audio.crossOrigin = "anonymous";

      incomingSoundRef.current = audio;
      return audio;
    };

    const fetchAudioBinary = async (signal: AbortSignal) => {
      if (!incomingSoundRef.current) return;
      const audio = incomingSoundRef.current;
      const sources = [INCOMING_SOUND_SRC, INCOMING_SOUND_FALLBACK].filter(Boolean) as string[];
      for (const src of sources) {
        try {
          const res = await fetch(src, { cache: "no-store", signal });
          if (!res.ok) continue;
          const blob = await res.blob();
          if (!blob.size) continue;
          const url = URL.createObjectURL(blob);
          incomingSoundUrlRef.current = url;
          audio.src = url;
          audio.load();
          return;
        } catch (err: any) {
          if (err?.name === "AbortError") return;
          continue;
        }
      }
    };

    const audio = setupAudio();
    // fallback imediato para nao depender do fetch assÃ­ncrono
    const initialSrc = INCOMING_SOUND_SRC || INCOMING_SOUND_FALLBACK;
    if (initialSrc) {
      audio.src = initialSrc;
      audio.load();
    }
    const aborter = new AbortController();
    fetchAudioBinary(aborter.signal);

    const unlock = () => {
      const tryPlay = () => {
        audio.currentTime = 0;
        audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
        }).catch(() => {
          /* autoplay block ignored */
        });
      };

      if (!incomingSoundUnlockedRef.current) {
        incomingSoundUnlockedRef.current = true;
      }
      audio.muted = true;
      tryPlay();
      audio.muted = false;

      if (audio.readyState < 2) {
        audio.load();
        audio.onloadeddata = () => {
          tryPlay();
          audio.onloadeddata = null;
        };
      } else {
        tryPlay();
      }
    };

    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      aborter.abort();
      if (incomingSoundUrlRef.current) {
        URL.revokeObjectURL(incomingSoundUrlRef.current);
        incomingSoundUrlRef.current = null;
      }
      window.removeEventListener("click", unlock);
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
      };
  }, []);

  const toAnchorRect = (rect: DOMRect) => ({
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    right: rect.right + window.scrollX,
    bottom: rect.bottom + window.scrollY,
    width: rect.width,
    height: rect.height
  });

  const updateMenuPosition = (
    anchor: { top: number; left: number; right: number; bottom: number; width: number; height: number },
    expanded: boolean
  ) => {
    const menuWidth = 180;
    const menuHeight = expanded ? 260 : 200;
    const viewportTop = window.scrollY;
    const viewportBottom = window.scrollY + window.innerHeight;
    let top = anchor.bottom + 4;
    if (top + menuHeight > viewportBottom - 12) {
      top = anchor.top - menuHeight - 4;
    }
    if (top < viewportTop + 8) {
      top = viewportTop + 8;
    }
    let left = anchor.right - menuWidth;
    const maxLeft = window.scrollX + window.innerWidth - menuWidth - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < window.scrollX + 8) left = window.scrollX + 8;
    setMenuPosition({ top, left });
  };

  const token = useMemo(() => localStorage.getItem("va_token") || "", []);
  const authHeaders = useMemo((): Record<string, string> => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(null);

  const fetchWhatsAppStatus = useCallback(async () => {
    try {
      const base = whatsappApiBase || WHATSAPP_API;
      const status = await fetchWhatsAppStatusWithFallback(base, authHeaders);
      if (status) {
        setWhatsappStatus(status);
        return;
      }
    } catch (err) {
      console.error("Erro ao buscar status WhatsApp:", err);
    }
    setWhatsappStatus(null);
  }, [authHeaders]);

  useEffect(() => {
    fetchWhatsAppStatus();
    const interval = setInterval(fetchWhatsAppStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchWhatsAppStatus]);

  const selectedTicket = tickets.find(t => t.id === selectedTicketId) || null;
  const filteredTickets = useMemo(() => {
    const base =
      activeTab === "pending" ? pendingTickets :
      activeTab === "mine" ? myTickets :
      isAdmin ? allActiveTickets : myTickets;
    const filtered = base.filter(t => {
      const term = ticketSearch.trim().toLowerCase();
      if (!term) return true;
      return (
        t.customerName.toLowerCase().includes(term) ||
        t.phone.toLowerCase().includes(term) ||
        t.id.toLowerCase().includes(term)
      );
    });
    return [...filtered].sort((a, b) => {
      const aTime = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
      const bTime = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
      return bTime - aTime;
    });
  }, [activeTab, allActiveTickets, myTickets, pendingTickets, ticketSearch]);

  const reloadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const buildUrl = (mode: "pending" | "mine" | "all") => {
        const qs = new URLSearchParams();
        qs.set("limit", "200");
        if (debouncedSearch) qs.set("search", debouncedSearch);
        if (mode === "pending") {
          qs.set("statuses", "aguardando_atendimento");
        } else if (mode === "mine") {
          qs.set("mine", "1");
          qs.set("statuses", "aguardando_atendimento,em_atendimento,aguardando_resposta_corretor");
        } else {
          qs.set("statuses", "aguardando_atendimento,em_atendimento,aguardando_resposta_corretor");
        }
        return `${CORE_API}/atendimentos/list?${qs.toString()}`;
      };

      const pendingPromise = isAdmin ? Promise.resolve(null) : fetch(buildUrl("pending"), { headers: { ...authHeaders } });
      const minePromise = isAdmin ? Promise.resolve(null) : fetch(buildUrl("mine"), { headers: { ...authHeaders } });
      const allPromise = isAdmin ? fetch(buildUrl("all"), { headers: { ...authHeaders } }) : Promise.resolve(null);

      const [pendingRes, mineRes, allRes] = await Promise.all([pendingPromise, minePromise, allPromise]);

      const pendingJson = pendingRes && (pendingRes as Response).ok ? await (pendingRes as Response).json() : { atendimentos: [] };
      const mineJson = mineRes && (mineRes as Response).ok ? await (mineRes as Response).json() : { atendimentos: [] };
      const allJson = allRes && (allRes as Response).ok ? await (allRes as Response).json() : { atendimentos: [] };
      const merged: Record<number, Ticket> = {};
      const source = isAdmin
        ? (allJson.atendimentos || [])
        : [...(pendingJson.atendimentos || []), ...(mineJson.atendimentos || [])];
      const buildAndMerge = (a: ApiAtendimento) => {
        const t = buildTicket(a);
        merged[a.id] = { ...merged[a.id], ...t };
      };
      source.forEach(buildAndMerge);
      setTickets(Object.values(merged));
    } catch (err) {
      console.error("Erro ao carregar atendimentos", err);
    } finally {
      setTicketsLoading(false);
    }
  }, [authHeaders, debouncedSearch, isAdmin]);

  useEffect(() => {
    reloadTickets();
  }, [reloadTickets]);

  // Carrega cat/produtos apenas 1x (evita sobrecarga a cada busca)
  useEffect(() => {
    const loadStatic = async () => {
      try {
        const [catRes, produtosRes] = await Promise.all([
          fetch(`${CORE_API}/atendimentos/categorias`, { headers: { ...authHeaders } }),
          fetch(`${CORE_API}/produtos?ativo=true`, { headers: { ...authHeaders } })
        ]);
        const catJson = catRes.ok ? await catRes.json() : { categorias: [] };
        const produtosJson = produtosRes.ok ? await produtosRes.json() : { produtos: [] };
        setCategories(catJson.categorias || []);
        setProducts((produtosJson.produtos || []).map((p: any) => ({ id: p.id, name: p.nome })));
      } catch (err) {
        console.error("Erro ao carregar dados estaticos", err);
      }
    };
    loadStatic();
  }, [authHeaders]);

  useEffect(() => {
    let active = true;
    const fetchMessages = async () => {
      if (!selectedTicket) {
        setMessagesLoading(false);
        return;
      }
      setMessagesLoading(true);
      try {
        const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/mensagens?limit=${MESSAGES_PAGE_SIZE}`, { headers: { ...authHeaders } });
        if (!res.ok || !active) return;
        const json = await res.json();
          if (!active) return;
        const msgs: Message[] = (json.mensagens || [])
          .filter((m: ApiMensagem) => !shouldIgnoreApiMensagem(m))
          .map(mapMensagem);
        setMessagesCursor(json?.nextBeforeId ?? null);
        setHasMoreMessages(!!json?.nextBeforeId);
        setTickets(prev => prev.map(t => (t.id === selectedTicket.id ? { ...t, messages: msgs, unreadCount: 0 } : t)));
        scrollToBottom();
      } catch (err) {
        console.error("Erro ao buscar mensagens", err);
      } finally {
        if (active) setMessagesLoading(false);
      }
    };
    fetchMessages();
    return () => {
      active = false;
    };
  }, [selectedTicket?.id, authHeaders]);

  const loadOlderMessages = useCallback(async () => {
    if (!selectedTicket || !messagesCursor || loadingMoreMessages) return;
    setLoadingMoreMessages(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    const prevScrollTop = container?.scrollTop || 0;
    try {
      const res = await fetch(
        `${CORE_API}/atendimentos/${selectedTicket.rawId}/mensagens?limit=${MESSAGES_PAGE_SIZE}&beforeId=${messagesCursor}`,
        { headers: { ...authHeaders } }
      );
      if (!res.ok) return;
      const json = await res.json();
      const older: Message[] = (json.mensagens || [])
        .filter((m: ApiMensagem) => !shouldIgnoreApiMensagem(m))
        .map(mapMensagem);
      const nextCursor = json?.nextBeforeId ?? null;
      setMessagesCursor(nextCursor);
      setHasMoreMessages(!!nextCursor);
      suppressAutoScrollRef.current = true;
      setTickets(prev => prev.map(t => {
        if (t.id !== selectedTicket.id) return t;
        const existingIds = new Set(t.messages.map(m => m.id));
        const existingWhatsapps = new Set(
          t.messages.map(m => m.whatsappMsgId).filter(Boolean) as string[]
        );
        const filtered = older.filter(m => {
          if (existingIds.has(m.id)) return false;
          if (m.whatsappMsgId && existingWhatsapps.has(m.whatsappMsgId)) return false;
          return true;
        });
        return { ...t, messages: [...filtered, ...t.messages] };
      }));
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        }
        suppressAutoScrollRef.current = false;
      });
    } catch (err) {
      console.error("Erro ao buscar mensagens anteriores", err);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [selectedTicket?.id, selectedTicket?.rawId, messagesCursor, authHeaders, loadingMoreMessages]);

  useEffect(() => {
    if (!selectedTicket) return;
    if (!selectedTicket.phone) return;
    let active = true;
    const lookup = async () => {
      try {
        const res = await fetch(`${CORE_API}/corretores/lookup?telefone=${encodeURIComponent(selectedTicket.phone)}`, { headers: { ...authHeaders } });
        if (!res.ok) return;
        const json = await res.json();
        if (!active || !json?.corretor) return;
        setTickets(prev => prev.map(t => t.rawId === selectedTicket.rawId ? {
          ...t,
          imobiliariaId: json.corretor.imobiliariaId ?? null,
          imobiliariaNome: json.corretor.imobiliariaNome ?? null,
          brokerId: json.corretor.id ?? t.brokerId
        } : t));
      } catch (err) {
        console.error("Falha ao resolver corretor por telefone", err);
      }
    };
    if (!selectedTicket.imobiliariaId && !selectedTicket.imobiliariaNome) {
      lookup();
    }
    return () => { active = false; };
  }, [selectedTicket?.rawId, selectedTicket?.imobiliariaId, selectedTicket?.imobiliariaNome, selectedTicket?.phone, authHeaders]);

  useEffect(() => {
    if (!selectedTicket?.brokerId) return;
    let active = true;
    const refresh = async () => {
      try {
        const res = await fetch(`${CORE_API}/corretores/${selectedTicket.brokerId}`, { headers: { ...authHeaders } });
        if (!res.ok) return;
        const json = await res.json().catch(() => ({}));
        const corretor = json?.corretor;
        if (!active || !corretor) return;
        setTickets(prev => prev.map(t => t.rawId === selectedTicket.rawId ? {
          ...t,
          imobiliariaId: corretor.imobiliariaId ?? corretor.imobiliaria_id ?? null,
          imobiliariaNome: corretor.imobiliariaNome ?? corretor.imobiliaria_nome ?? null
        } : t));
      } catch (err) {
        console.error("Falha ao atualizar imobiliaria do corretor", err);
      }
    };
    refresh();
    return () => { active = false; };
  }, [selectedTicket?.brokerId, selectedTicket?.rawId, authHeaders]);

  useEffect(() => {
    if (!selectedTicketId) return;
    if (suppressAutoScrollRef.current) return;
    scrollToBottom();
  }, [selectedTicketId, tickets]);

  useEffect(() => {
    setSendError(null);
    setTransferError(null);
  }, [selectedTicketId]);

  useEffect(() => {
    // Limpa contexto de reply/anexo ao trocar de atendimento
    setReplyTo(null);
    setMenuMessageId(null);
    setAttachment(null);
    setInputText("");
    setMessagesCursor(null);
    setHasMoreMessages(false);
    setLoadingMoreMessages(false);
  }, [selectedTicketId]);

  const loadImobiliariasByProduto = async (produtoId: string) => {
    if (!produtoId) {
      setImobiliarias([]);
      setRegisterData(prev => ({ ...prev, imobiliariaId: "" }));
      return;
    }
    try {
      const res = await fetch(`${CORE_API}/produtos/${produtoId}/imobiliarias`, { headers: { ...authHeaders } });
      if (!res.ok) {
        setImobiliarias([]);
        return;
      }
      const json = await res.json();
      setImobiliarias((json.imobiliarias || []).map((i: any) => ({ id: i.id, name: i.nome || i.nome_fantasia || "Imobiliaria" })));
    } catch (err) {
      console.error("Erro ao buscar imobiliarias", err);
      setImobiliarias([]);
    }
  };

  useEffect(() => {
    loadImobiliariasByProduto(registerData.productId);
  }, [registerData.productId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const uploadMedia = async (file: File, caption: string, quotedMsgId?: string | null) => {
    if (!selectedTicket) return;
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({ dataUrl, fileName: file.name })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Falha no upload");
      }
      const json = await res.json();
      await sendMessageWithMedia(json.mediaUrl, json.mediaType, caption, json.mediaMime, quotedMsgId);
    } finally {
      setUploading(false);
    }
  };

  const sendMessageWithMedia = async (mediaUrl: string, mediaType: string, caption: string, mediaMime?: string, quotedMsgId?: string | null) => {
    if (!selectedTicket) return;
    const payload: any = { to: selectedTicket.phone, mediaUrl, mediaType, message: caption, mediaMime, quotedMsgId };
    setSendError(null);
    const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/mensagens`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Falha ao enviar midia");
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const previewUrl = URL.createObjectURL(file);
    setAttachment({ file, previewUrl, mime: file.type || "", name: file.name });
  };

  const stopCurrentRecording = () => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        /* ignore */
      }
    }
  };

  const startRecording = async () => {
    setRecordingError(null);
    try {
      const oggMime = "audio/ogg;codecs=opus";
      const useOgg = MediaRecorder.isTypeSupported(oggMime);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = useOgg ? new MediaRecorder(stream, { mimeType: oggMime }) : new MediaRecorder(stream);
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        // Usa o mimeType real escolhido pelo MediaRecorder; backend converte se nao for ogg/opus
        const mimeType = recorder.mimeType || (useOgg ? oggMime : "audio/webm");
        const blob = new Blob(recordingChunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          setRecordingError("Nenhum audio capturado");
          return;
        }
        const ext = mimeType.includes("ogg") ? "ogg" : (mimeType.includes("webm") ? "webm" : "audio");
        const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: mimeType });
        const previewUrl = URL.createObjectURL(file);
        setAttachment({ file, previewUrl, mime: file.type || mimeType, name: file.name });
        setIsRecording(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err: any) {
      setRecordingError(err?.message || "Nao foi possivel acessar o microfone");
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    stopCurrentRecording();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          handleFiles({ 0: file, length: 1, item: () => file } as unknown as FileList);
          break;
        }
      }
    }
  };

  const openPreview = (url?: string, type?: string, text?: string) => {
    if (!url) return;
    setPreviewMedia({ url, type: (type || "").toUpperCase(), text });
    setPreviewError(null);
    setPreviewZoom(1);
  };

  const renderMessageContent = (msg: Message) => {
    const mediaType = (msg.mediaType || "").toUpperCase();
    const mediaSrc = normalizeMediaUrl(msg.mediaUrl || undefined, token);
    const mapMatch = msg.text ? msg.text.match(/https?:\/\/www\.google\.com\/maps\?q=[^\s]+/i) : null;
    const mapUrl = mapMatch ? mapMatch[0] : null;
    const parseContact = (text: string) => {
      const normalizeDigits = (value: string) => value.replace(/\D/g, "");
      const vcardName = text.match(/^FN:(.+)$/m)?.[1]?.trim();
      const telRaw = text.match(/^(?:\\w+\\.)?TEL[^:]*:(.+)$/m)?.[1]?.trim();
      const tel = telRaw ? normalizeDigits(telRaw) : "";
      if (vcardName || tel) return { name: vcardName || "Contato", phone: tel || "" };
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length >= 2) return { name: lines[0], phone: normalizeDigits(lines[1]) || lines[1] };
      if (lines.length === 1) return { name: lines[0], phone: "" };
      return { name: "Contato", phone: "" };
    };
    const renderText = (text: string) => (
      <p className={`text-sm whitespace-pre-wrap break-all ${msg.deleted ? "line-through text-gray-500 dark:text-gray-400" : ""}`}>
        <span dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>").replace(/\*(.*?)\*/g, "<b>$1</b>") }} />
      </p>
    );
    const deletedNote = msg.deleted ? (
      <p className="text-[11px] italic text-gray-500 dark:text-gray-400 mt-1">Mensagem apagada</p>
    ) : null;

    if (mediaSrc && (mediaType === "IMAGE" || mediaType === "STICKER")) {
      return (
        <div className="space-y-1">
          <img
            src={mediaSrc}
            alt={msg.text || (mediaType === "STICKER" ? "Figurinha" : "Imagem")}
            className="max-w-full md:max-w-xs rounded-lg border border-black/5 cursor-zoom-in"
            onError={(e) => (e.currentTarget.style.display = "none")}
            onClick={() => openPreview(mediaSrc, mediaType, msg.text)}
          />
          {msg.text && renderText(msg.text)}
          {deletedNote}
        </div>
      );
    }

    if (mediaSrc && mediaType === "VIDEO") {
      return (
        <div className="space-y-1">
          <video
            controls
            className="w-full max-w-xs md:max-w-sm rounded-lg cursor-pointer"
            style={{ maxHeight: 260 }}
            src={mediaSrc}
            onClick={() => openPreview(mediaSrc, mediaType, msg.text)}
          />
          {msg.text && renderText(msg.text)}
          {deletedNote}
        </div>
      );
    }

    if (mediaSrc && mediaType === "AUDIO") {
      return (
        <div className="space-y-1">
          <audio controls className="w-64 max-w-full" src={mediaSrc} style={{ minWidth: 180 }} />
          {msg.text && renderText(msg.text)}
          {deletedNote}
        </div>
      );
    }

    if (mediaSrc && mediaType === "DOCUMENT") {
      return (
        <div className="space-y-1">
          <a
            href={mediaSrc}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            Baixar arquivo
          </a>
          {msg.text && renderText(msg.text)}
          {deletedNote}
        </div>
      );
    }

    if (mapUrl || mediaType === "LOCATION") {
      const prefix = (msg.text || "");
      const finalPrefix = mapUrl ? prefix.replace(mapUrl, "").trim() : prefix.trim();
      return (
        <div className="space-y-1">
          {finalPrefix ? renderText(finalPrefix) : null}
          <a
            href={mapUrl || undefined}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            Ver localizacao no Google Maps
          </a>
          {deletedNote}
        </div>
      );
    }

    if (mediaType === "CONTACT") {
      const contact = parseContact(msg.text || "");
      return (
        <div className={`rounded-lg border px-3 py-2 ${darkMode ? "border-vision-border bg-black/30" : "border-gray-200 bg-gray-50"}`}>
          <div className="text-xs uppercase tracking-wide text-gray-500">Contato recebido</div>
          <div className="text-sm font-semibold">{contact.name || "Contato"}</div>
          {contact.phone ? <div className="text-sm text-gray-600">{contact.phone}</div> : null}
          {deletedNote}
        </div>
      );
    }

    return (
      <>
        {renderText(msg.text || "(mensagem)")}
        {deletedNote}
      </>
    );
  };

  const markMessageDeleted = (atendimentoId: number, opts: { mensagemId?: number; whatsappMsgId?: string; deletedAt?: string | null; deletedBy?: number | null }) => {
    setTickets(prev => prev.map(t => {
      if (t.rawId !== atendimentoId) return t;
      return {
        ...t,
        messages: t.messages.map(m => {
          const match =
            (opts.mensagemId && m.id === opts.mensagemId) ||
            (opts.whatsappMsgId && m.whatsappMsgId === opts.whatsappMsgId);
          if (!match) return m;
          return {
            ...m,
            deleted: true,
            deletedAt: opts.deletedAt || m.deletedAt || new Date().toISOString(),
            deletedBy: opts.deletedBy ?? m.deletedBy ?? null,
            reactions: [],
            reaction: undefined,
            reactionSource: undefined,
            reactionActorName: null
          };
        })
      };
    }));
  };

  const playIncomingSound = useCallback(() => {
    const audio = incomingSoundRef.current;
    if (!audio) return;
    try {
      if (audio.readyState < 2) {
        audio.load();
        audio.oncanplaythrough = () => {
          audio.oncanplaythrough = null;
          audio.currentTime = 0;
          const res = audio.play();
          if (res && typeof (res as any).catch === "function") {
            (res as Promise<void>).catch(() => {});
          }
        };
      } else {
        audio.currentTime = 0;
        const playResult = audio.play();
        if (playResult && typeof (playResult as any).catch === "function") {
          (playResult as Promise<void>).catch(() => {});
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const hydrateTicketById = async (id: number) => {
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${id}`, { headers: { ...authHeaders } });
      if (!res.ok) return;
      const json = await res.json();
      if (!json?.atendimento) return;
      const hydrated = buildTicket(json.atendimento as ApiAtendimento);
      setTickets(prev => {
        const exists = prev.some(t => t.rawId === id);
        if (exists) {
          return prev.map(t => (t.rawId === id ? { ...t, ...hydrated } : t));
        }
        return [...prev, hydrated];
      });
    } catch (err) {
      console.error("Falha ao hidratar atendimento transferido", err);
    }
  };

  useEffect(() => {
    if (!token) return;
    const coreBase = (CORE_API || "").replace(/\/$/, "");
    const url = `${coreBase}/atendimentos/events?token=${encodeURIComponent(token)}`;
    // setSseUrl(url);
    // setSseError(null);
    // setSseStatus("connecting");

    const source = new EventSource(url);
    source.onopen = () => {
      // setSseStatus("open");
      // setSseError(null);
    };
    source.onerror = (err: Event | any) => {
      console.error("SSE Error:", err);
      // setSseStatus("error");
      // const msg = err?.message || err?.target?.readyState === 2 ? "Conex?o fechada" : "Conex?o SSE falhou";
      // setSseError(msg);
    };
    source.onmessage = () => {};
    source.addEventListener("mensagem.sistema", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.atendimentoId) return;
        setTickets(prev =>
          prev.map(t => {
            if (t.rawId !== data.atendimentoId) return t;
            const exists = t.messages.some(m => (data.mensagemId && m.id === data.mensagemId));
            if (exists) return t;
            const sysApi: ApiMensagem = {
              id: data.mensagemId || Date.now(),
              atendimento_id: data.atendimentoId,
              direction: "SYS",
              whatsapp_msg_id: null,
              body: data.body || "",
              media_type: "TEXT",
              media_url: null,
              sender_name: data.atendenteNome || null,
              sent_at: data.sentAt || null,
              quoted_msg_id: null,
              reaction: null,
              apagada: false,
              apagada_em: null,
              apagada_por: null
            };
            return { ...t, messages: [...t.messages, mapMensagem(sysApi)] };
          })
        );
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("mensagem.recebida", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.atendimentoId) return;
        const shouldIgnore =
          !String(data.body || "").trim() &&
          !data.mediaUrl &&
          String(data.mediaType || "").toUpperCase() === "OTHER";
        if (shouldIgnore) return;
        let addedMessage = false;
        onNewMessage?.();
        const receivedAt = data.sentAt ? new Date(data.sentAt).toISOString() : new Date().toISOString();
        setTickets(prev =>
          prev.map(t =>
            t.rawId === data.atendimentoId
              ? {
                  ...t,
                  unreadCount: t.unreadCount + 1,
                  lastUpdate: formatTime(receivedAt),
                  lastMessageAt: receivedAt,
                  messages: (() => {
                    const exists = t.messages.some(m =>
                      (data.mensagemId && m.id === data.mensagemId) ||
                      (data.whatsappMsgId && m.whatsappMsgId === data.whatsappMsgId)
                    );
                    if (exists) return t.messages;
                    addedMessage = true;
                    return [
                      ...t.messages,
                      {
                        id: data.mensagemId || data.id || Date.now(),
                        text: data.body || (data.mediaType === "STICKER" ? "VocÃª recebeu um sticker" : "(mensagem recebida)"),
                        sender: "customer",
                        time: formatTime(new Date().toISOString()),
                        status: "read",
                        mediaType: data.mediaType,
                        mediaUrl: data.mediaUrl || undefined,
                        whatsappMsgId: data.whatsappMsgId || data.messageId || undefined,
                        replyToId: data.quotedMsgId || null,
                        reaction: data.reaction || undefined,
                        reactionSource: (data as any).source === "whatsapp" ? "whatsapp" : undefined,
                        reactionActorName: (data as any).actorName || null,
                        reactions: data.reaction ? upsertReaction([], data.reaction, (data as any).source === "whatsapp" ? "whatsapp" : undefined, (data as any).actorName) : [],
                        deleted: false
                      }
                    ];
                  })()
                }
              : t
          )
        );
        if (addedMessage) {
          playIncomingSound();
        }
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("mensagem.enviada", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.atendimentoId) return;
        const shouldIgnore =
          !String(data.body || "").trim() &&
          !data.mediaUrl &&
          String(data.mediaType || "").toUpperCase() === "OTHER";
        if (shouldIgnore) return;
        setTickets(prev =>
          prev.map(t =>
            t.rawId === data.atendimentoId
              ? {
                  ...t,
                  messages: (() => {
                    const newMsg: Message = {
                      id: data.mensagemId || data.id || Date.now(),
                      text: data.body || "(mensagem enviada)",
                      sender: "me",
                      time: formatTime(new Date().toISOString()),
                      status: "sent",
                      mediaType: data.mediaType,
                      mediaUrl: data.mediaUrl || undefined,
                      whatsappMsgId: data.whatsappMsgId || undefined,
                      replyToId: data.quotedMsgId || null,
                      reaction: data.reaction || undefined,
                      reactionSource: (data as any).source === "desk" ? "desk" : undefined,
                      reactionActorName: (data as any).actorName || null,
                      reactions: data.reaction ? upsertReaction([], data.reaction, (data as any).source === "desk" ? "desk" : undefined, (data as any).actorName) : [],
                      deleted: false
                    };

                    const exists = data.whatsappMsgId || data.mensagemId
                      ? t.messages.some(m =>
                          (data.mensagemId && m.id === data.mensagemId) ||
                          (data.whatsappMsgId && m.whatsappMsgId === data.whatsappMsgId)
                        )
                      : false;
                    if (exists) return t.messages;

                    const optimisticIdx = t.messages.findIndex(m =>
                      m.sender === "me" &&
                      !m.whatsappMsgId &&
                      (m.text || "(mensagem enviada)") === (data.body || "(mensagem enviada)") &&
                      (!!m.mediaUrl === !!data.mediaUrl)
                    );
                    if (optimisticIdx >= 0) {
                      const copy = [...t.messages];
                      copy[optimisticIdx] = { ...copy[optimisticIdx], ...newMsg };
                      return copy;
                    }

                    return [...t.messages, newMsg];
                  })()
                }
              : t
          )
        );
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("mensagem.reacao", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        const reaction = data.reaction;
        const targetId = data.mensagemId || data.id;
        const targetWhatsappId = data.whatsappMsgId || data.whatsapp_msg_id;
        if (!reaction || (!targetWhatsappId && !targetId)) return;
        setTickets(prev =>
          prev.map(t => ({
            ...t,
            messages: t.messages.map(m =>
              (!data.atendimentoId || t.rawId === data.atendimentoId) &&
              ((targetId && m.id === targetId) || (targetWhatsappId && m.whatsappMsgId === targetWhatsappId))
                ? {
                    ...m,
                    reaction,
                    reactionSource: (data as any).source === "whatsapp" ? "whatsapp" : (data as any).source === "desk" ? "desk" : m.reactionSource,
                    reactionActorName: (data as any).actorName || m.reactionActorName || null,
                    reactions: upsertReaction(m.reactions, reaction, (data as any).source === "whatsapp" ? "whatsapp" : (data as any).source === "desk" ? "desk" : undefined, (data as any).actorName || null)
                  }
                : m
            )
          }))
        );
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("mensagem.apagada", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.atendimentoId) return;
        markMessageDeleted(data.atendimentoId, {
          mensagemId: data.mensagemId,
          whatsappMsgId: data.whatsappMsgId,
          deletedAt: data.deletedAt,
          deletedBy: data.deletedBy ?? null
        });
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("atendimento.status", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.id || !data.status) return;
        setTickets(prev => prev.map(t => (t.rawId === data.id ? { ...t, status: mapStatus(data.status) } : t)));
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("atendimento.assumido", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.id) return;
        setTickets(prev => prev.map(t => (t.rawId === data.id ? {
          ...t,
          status: "open",
          produtoId: data.produtoId ?? t.produtoId,
          imobiliariaId: data.imobiliariaId ?? t.imobiliariaId,
          imobiliariaNome: data.imobiliariaNome ?? t.imobiliariaNome,
          agentId: data.atendenteId ?? t.agentId,
          agentName: data.atendenteNome ?? t.agentName
        } : t)));
        } catch {
          /* ignore */
        }
      });
    source.addEventListener("atendimento.transferido", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.id) return;
        let found = false;
        const isForMe = !!(userId && data.atendenteId === userId);
        setTickets(prev => prev.map(t => {
          if (t.rawId !== data.id) return t;
          found = true;
          return {
            ...t,
            agentId: data.atendenteId ?? t.agentId,
            agentName: data.atendenteNome ?? t.agentName,
            status: "open"
          };
        }));
        if (isForMe) {
          setActiveTab("mine");
        }
        if (!found) {
          // Cria um placeholder imediato enquanto hidrata do backend
          setTickets(prev => {
            const exists = prev.some(t => t.rawId === data.id);
            if (exists) return prev;
            const placeholder: Ticket = {
              rawId: data.id,
              id: `#${data.id}`,
              customerName: "Contato",
              customerAvatar: "CT",
              platform: "whatsapp",
              status: "open",
              waitingSince: "",
              lastUpdate: "",
              lastMessageAt: null,
              messages: [],
              unreadCount: 0,
              phone: "",
              agentId: data.atendenteId || null,
              agentName: data.atendenteNome || null
            };
            return [...prev, placeholder];
          });
        }
        if (isForMe || isAdmin || !found) {
          hydrateTicketById(data.id);
          if (isForMe) {
            reloadTickets(); // garante lista "meus" atualizada
          }
        }
      } catch {
        /* ignore */
      }
    });
    source.addEventListener("atendimento.novo", ev => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || "{}");
        if (!data.atendimentoId || !data.contatoWhatsApp) return;
        setTickets(prev => {
          const exists = prev.some(t => t.rawId === data.atendimentoId);
          if (exists) return prev;
          const novo: ApiAtendimento = {
            id: data.atendimentoId,
            contato_whatsapp: data.contatoWhatsApp,
            contato_nome: data.contatoNome || "",
            contato_foto_url: data.contatoFotoUrl || null,
            status: data.status || "aguardando_atendimento",
            created_at: data.createdAt,
            updated_at: data.updatedAt,
          };
          return [buildTicket(novo), ...prev];
        });
      } catch {
        /* ignore */
      }
    });
    return () => {
      // setSseStatus("closed");
      source.close();
    };
  }, [token]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket) return;
    if (!inputText.trim() && !attachment) {
      setSendError("Digite uma mensagem ou anexe uma midia");
      return;
    }
    const prefix = agentName ? `*${agentName}*\n` : "";
    const quotedMsgId = replyTo?.whatsappMsgId || null;
    if (attachment) {
      try {
        await uploadMedia(attachment.file, `${prefix}${inputText.trim()}`.trim(), quotedMsgId);
        URL.revokeObjectURL(attachment.previewUrl);
        setAttachment(null);
        setInputText("");
        setReplyTo(null);
      } catch (err) {
        console.error("Erro ao enviar midia", err);
        setSendError(err instanceof Error ? err.message : "Erro ao enviar midia");
      }
      return;
    }

    const payload: any = { to: selectedTicket.phone, message: `${prefix}${inputText}` };
    if (quotedMsgId) payload.quotedMsgId = quotedMsgId;

    // Otimista: adiciona no chat enquanto aguarda SSE
    const optimisticMessage: Message = {
      id: Date.now(),
      text: `${prefix}${inputText}`,
      sender: "me",
      time: formatTime(new Date().toISOString()),
      status: "sent",
      mediaType: "TEXT",
      mediaUrl: undefined,
      whatsappMsgId: undefined,
      replyToId: quotedMsgId,
      reaction: undefined,
      reactions: [],
      deleted: false
    };
    setTickets(prev =>
      prev.map(t =>
        t.id === selectedTicket.id
          ? { ...t, messages: [...t.messages, optimisticMessage] }
          : t
      )
    );

    setSendError(null);
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/mensagens`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Falha ao enviar");
      }

      setInputText("");
      setReplyTo(null);
    } catch (err) {
      console.error("Erro ao enviar mensagem", err);
      setSendError(err instanceof Error ? err.message : "Erro ao enviar mensagem");
    }
  };

  const handleReactMessage = async (msg: Message, emoji: string) => {
    if (!selectedTicket || !msg.id) return;
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/mensagens/${msg.id}/reagir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ emoji })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Falha ao reagir");
      }
    } catch (err) {
      console.error("Erro ao reagir", err);
      await Swal.fire({
        icon: "error",
        title: "Nao foi possivel reagir",
        text: err instanceof Error ? err.message : "Falha ao enviar reacao"
      });
    } finally {
      setMenuMessageId(null);
    }
  };

  const handleDeleteMessage = async (msg: Message) => {
    if (!selectedTicket || !msg.id) return;
    const confirmation = await Swal.fire({
      title: "Apagar mensagem?",
      text: "A mensagem sera apagada tambem no WhatsApp (quando permitido).",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Apagar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626"
    });
    if (!confirmation.isConfirmed) return;

    setDeletingMessageId(msg.id);
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/mensagens/${msg.id}/apagar`, {
        method: "POST",
        headers: { ...authHeaders }
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        const errorMsg = json?.error || "Falha ao apagar mensagem";
        throw new Error(errorMsg);
      }
      setMenuMessageId(null);
      markMessageDeleted(selectedTicket.rawId, { mensagemId: msg.id, whatsappMsgId: msg.whatsappMsgId || undefined, deletedAt: new Date().toISOString() });
      await Swal.fire({
        icon: "success",
        title: "Apagando mensagem",
        text: "Solicitacao enviada. Aguarde alguns segundos.",
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err: any) {
      await Swal.fire({
        icon: "error",
        title: "Erro ao apagar",
        text: err?.message || "Nao foi possivel apagar a mensagem"
      });
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handlePickUpTicket = async () => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/assumir`, {
        method: "POST",
        headers: { ...authHeaders }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errMsg = json?.error || "Falha ao assumir atendimento";
        throw new Error(errMsg);
      }
      const systemMsg = json?.systemMessage;
      setTickets(prev => prev.map(t => {
        if (t.id !== selectedTicket.id) return t;
        let nextMessages = t.messages;
        if (systemMsg?.body) {
          const sysApi: ApiMensagem = {
            id: systemMsg.id || Date.now(),
            atendimento_id: selectedTicket.rawId,
            direction: "SYS",
            whatsapp_msg_id: null,
            body: systemMsg.body,
            media_type: "TEXT",
            media_url: null,
            sender_name: systemMsg.atendente_nome || null,
            sent_at: systemMsg.sent_at || null,
            quoted_msg_id: null,
            reaction: null,
            apagada: false,
            apagada_em: null,
            apagada_por: null
          };
          const mapped = mapMensagem(sysApi);
          const hasId = t.messages.some(m => m.id === mapped.id);
          nextMessages = hasId ? t.messages : [...t.messages, mapped];
        }
        return { ...t, status: "open", messages: nextMessages };
      }));
      setActiveTab("mine");
    } catch (err) {
      console.error("Erro ao assumir atendimento", err);
    }
  };

  const ensureProdutoVinculado = async () => {
    if (!selectedTicket) return;
    const produtoId = Number(closeData.productId);
    if (!produtoId || selectedTicket.produtoId === produtoId) return;
    if (!selectedTicket.imobiliariaId) return;
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/vincular-corretor`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          nome: selectedTicket.customerName || selectedTicket.customerAvatar || "Contato",
          email: null,
          telefone: selectedTicket.phone,
          produtoId,
          imobiliariaId: selectedTicket.imobiliariaId,
          fotoUrl: selectedTicket.avatarUrl || null
        })
      });
      if (!res.ok) return;
      const json = await res.json().catch(() => ({}));
      setTickets(prev => prev.map(t => t.rawId === selectedTicket.rawId ? {
        ...t,
        produtoId,
        imobiliariaId: selectedTicket.imobiliariaId,
        imobiliariaNome: json?.imobiliariaNome || t.imobiliariaNome
      } : t));
    } catch (err) {
      console.error("Falha ao vincular produto ao encerrar", err);
    }
  };

  const handleStatusUpdate = async (status: "encerrado") => {
    if (!selectedTicket) return;
    if (!closeData.productId) {
      setSendError("Selecione um produto vinculado para encerrar.");
      return;
    }
    try {
      await ensureProdutoVinculado();
      await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ status, categoriaId: closeData.category ? Number(closeData.category) : undefined })
      });
      setTickets(prev => prev.map(t => (t.id === selectedTicket.id ? { ...t, status: "closed" } : t)));
      setIsCloseModalOpen(false);
      setSelectedTicketId(null);
    } catch (err) {
      console.error("Erro ao atualizar status", err);
    }
  };

  const handleQuickClosePending = async () => {
    if (!selectedTicket) return;
    const confirmed = window.confirm("Deseja fechar este atendimento sem assumir?");
    if (!confirmed) return;
    try {
      await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ status: "descartado" })
      });
      setTickets(prev => prev.filter(t => t.id !== selectedTicket.id));
      setSelectedTicketId(null);
    } catch (err) {
      console.error("Erro ao encerrar atendimento pendente", err);
    }
  };

  const hydrateProdutoSugerido = useCallback(async () => {
    if (!selectedTicket?.imobiliariaId) return;
    try {
      const res = await fetch(`${CORE_API}/imobiliarias/${selectedTicket.imobiliariaId}/produtos`, { headers: { ...authHeaders } });
      if (!res.ok) return;
      const json = await res.json();
      const first = (json?.produtos || [])[0];
      if (first?.id) {
        setCloseData(prev => ({ ...prev, productId: prev.productId || String(first.id) }));
        setTickets(prev => prev.map(t => t.rawId === selectedTicket.rawId ? { ...t, produtoId: first.id, produtoNome: first.nome || t.produtoNome } : t));
      }
    } catch (err) {
      console.error("Falha ao sugerir produto da imobiliaria", err);
    }
  }, [CORE_API, authHeaders, selectedTicket?.imobiliariaId, selectedTicket?.rawId]);

  const handleOpenCloseModal = async () => {
    const defaultProduct = selectedTicket?.produtoId ? String(selectedTicket.produtoId) : "";
    setCloseData({ reason: "", category: "", productId: defaultProduct });
    setIsCloseModalOpen(true);
    if (!defaultProduct) {
      await hydrateProdutoSugerido();
    }
  };

  const handleOpenTransferModal = () => {
    setTransferData({ atendenteId: "" });
    setTransferError(null);
    setIsTransferModalOpen(true);
    // fetch staff/admin users for select
    (async () => {
      try {
        const res = await fetch(`${CORE_API}/usuarios`, { headers: { ...authHeaders } });
        if (!res.ok) return;
        const json = await res.json();
        const users = (json.usuarios || [])
          .filter((u: any) => ["ADMIN", "STAFF"].includes((u.tipo || "").toUpperCase()))
          .map((u: any) => ({ id: u.id, nome: u.nome || `Usuario ${u.id}` }));
        setTransferUsers(users);
      } catch (err) {
        console.error("Erro ao carregar atendentes", err);
      }
    })();
  };

  const handleOpenRegisterModal = () => {
    if (selectedTicket) {
      setRegisterData({
        name: selectedTicket.customerName,
        email: "",
        productId: "",
        imobiliariaId: "",
        brokerType: "imobiliaria"
      });
    }
    setImobiliariaSearch("");
    setIsImobiliariaDropdownOpen(false);
    setIsRegisterModalOpen(true);
  };

  const handleTransferSubmit = async () => {
    if (!selectedTicket || !transferData.atendenteId.trim()) return;
    setTransferLoading(true);
    setTransferError(null);
    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/transferir`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({ atendenteId: Number(transferData.atendenteId) })
      });
      const bodyText = await res.text();
      if (!res.ok) {
        throw new Error(bodyText || "Falha ao transferir atendimento");
      }
      setTickets(prev => prev.map(t => (t.id === selectedTicket.id ? { ...t, status: "open" } : t)));
      setIsTransferModalOpen(false);
      setTransferData({ atendenteId: "" });
      setSelectedTicketId(null);
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : "Erro ao transferir atendimento");
    } finally {
      setTransferLoading(false);
    }
  };

  const handleRegisterSubmit = async () => {
    if (!selectedTicket) return;
    if (!registerData.name.trim() || !registerData.productId) return;
    const requiresImobiliaria = registerData.brokerType === "imobiliaria";
    const hasImobiliaria = Boolean(registerData.imobiliariaId);
    if (requiresImobiliaria && !hasImobiliaria) return;

    try {
      const res = await fetch(`${CORE_API}/atendimentos/${selectedTicket.rawId}/vincular-corretor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({
          nome: registerData.name.trim(),
          email: registerData.email?.trim() || null,
          telefone: selectedTicket.phone,
          produtoId: Number(registerData.productId),
          imobiliariaId: hasImobiliaria ? Number(registerData.imobiliariaId) : null,
          cargo: registerData.brokerType === "cliente" ? "CLIENTE" : "CORRETOR",
          fotoUrl: selectedTicket.avatarUrl || null
        })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Falha ao vincular corretor");
      }
      const json = await res.json().catch(() => ({}));
      setIsRegisterModalOpen(false);
      // Atualiza ticket local com imobiliaria/produto e mostra sucesso
      setTickets(prev => prev.map(t => t.rawId === selectedTicket.rawId ? {
        ...t,
        produtoId: Number(registerData.productId),
        imobiliariaId: hasImobiliaria ? Number(registerData.imobiliariaId) : null,
        imobiliariaNome: hasImobiliaria ? (json?.imobiliariaNome || imobiliarias.find(i => String(i.id) === String(registerData.imobiliariaId))?.name || t.imobiliariaNome) : null,
        brokerId: json?.corretorId || t.brokerId,
        avatarUrl: t.avatarUrl || selectedTicket.avatarUrl
      } : t));
      await Swal.fire({
        icon: "success",
        title: "Vinculado com sucesso",
        text: "Corretor associado ao produto/imobiliaria.",
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Erro ao vincular corretor", err);
      await Swal.fire({
        icon: "error",
        title: "Erro",
        text: err instanceof Error ? err.message : "Nao foi possivel vincular corretor"
      });
    }
  };

  return (
    <>
      {previewMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setPreviewMedia(null)}>
          <div
            className="relative w-full h-full max-w-5xl max-h-[90vh] flex flex-col items-center justify-center bg-black/60 rounded-2xl border border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewMedia(null)}
              className="absolute top-3 right-3 text-white bg-black/50 rounded-full px-3 py-1 text-sm hover:bg-black/70"
            >
              Fechar
            </button>
            {previewMedia.type === "IMAGE" && (
              <div className="flex flex-col items-center gap-2 w-full h-full p-4 overflow-auto">
                <div className="flex items-center gap-2 text-white text-xs">
                  <button
                    onClick={() => setPreviewZoom(z => Math.max(0.5, z - 0.1))}
                    className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                  >
                    -
                  </button>
                  <span>{Math.round(previewZoom * 100)}%</span>
                  <button
                    onClick={() => setPreviewZoom(z => Math.min(3, z + 0.1))}
                    className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                  >
                    +
                  </button>
                  <button
                    onClick={() => setPreviewZoom(1)}
                    className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                  >
                    Reset
                  </button>
                </div>
                <div className="flex-1 overflow-auto w-full flex items-center justify-center">
                  <img
                    src={previewMedia.url}
                    alt={previewMedia.text || "Imagem"}
                    className="max-w-none rounded-lg"
                    style={{ transform: `scale(${previewZoom})`, transformOrigin: "center center" }}
                    onError={() => setPreviewError("Falha ao carregar a imagem")}
                  />
                </div>
                {previewError && (
                  <div className="text-red-400 text-sm">{previewError}</div>
                )}
                {previewMedia.text && <p className="text-white text-sm mt-2">{previewMedia.text}</p>}
              </div>
            )}
            {previewMedia.type === "VIDEO" && (
              <div className="w-full h-full p-4 flex items-center justify-center">
                <video
                  controls
                  className="w-full h-full max-h-[80vh] rounded-lg"
                  src={previewMedia.url}
                  onError={() => setPreviewError("Falha ao carregar o video")}
                />
                {previewError && <div className="text-red-400 text-sm p-2">{previewError}</div>}
              </div>
            )}
            {previewError && !previewMedia.type && (
              <div className="text-red-400 text-sm p-4">{previewError}</div>
            )}
          </div>
        </div>
      )}
      {avatarPreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setAvatarPreviewUrl(null)}>
          <div className="relative max-w-[80vw] max-h-[80vh] rounded-2xl border border-white/10 bg-black/60 p-3">
            <img
              src={avatarPreviewUrl}
              alt="Foto do corretor"
              className="max-h-[70vh] max-w-[70vw] rounded-xl object-contain"
            />
          </div>
        </div>
      )}

    <div className={`h-full flex flex-col md:flex-row overflow-hidden ${darkMode ? "text-gray-100 bg-vision-darkGreen" : "text-gray-800 bg-gray-50"}`}>
      <div className={`w-full md:w-80 border-r flex-col ${selectedTicketId ? "hidden md:flex" : "flex"} ${darkMode ? "border-vision-border bg-black" : "border-gray-200 bg-white"}`}>
        <div className={`p-4 border-b ${darkMode ? "border-vision-border" : "border-gray-100"}`}>
          <div className="flex items-center gap-2 mb-4 px-2">
            <span className="relative group inline-flex items-center">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${whatsappStatus?.status === "ready" ? "bg-green-500" : "bg-red-500"}`}
                aria-label={whatsappStatus?.status === "ready" ? "WhatsApp conectado" : "WhatsApp desconectado"}
              />
              <span
                className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-semibold shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100
                  ${darkMode ? "bg-vision-surface text-gray-200 border border-vision-border" : "bg-white text-gray-700 border border-gray-200"}
                `}
              >
                Status: {whatsappStatus?.status || "desconhecido"}
              </span>
            </span>
            <h2 className="text-xl font-bold">Central de Atendimento</h2>
          </div>
          <div className={`flex p-1 rounded-lg mb-4 gap-1 sm:gap-2 flex-wrap sm:flex-nowrap ${darkMode ? "bg-vision-surface" : "bg-gray-100"}`}>
              <button
                onClick={() => setActiveTab("pending")}
                className={`flex-1 basis-0 min-w-0 px-2 py-1 sm:py-1.5 text-[11px] sm:text-sm font-medium rounded-md flex items-center justify-center gap-1.5 transition-all ${activeTab === "pending" ? (darkMode ? "bg-vision-darkGreen text-white shadow-sm" : "bg-white text-[#294b29] shadow-sm") : "text-gray-500 hover:text-gray-700"}`}
              >
                <Inbox size={16} className="shrink-0" />
                {isAdmin ? null : <span className="hidden sm:inline">Pendentes</span>}
                <span className={`text-[11px] sm:text-xs px-1.5 py-0.5 rounded-full ${darkMode ? "bg-vision-surface" : "bg-gray-200"}`}>
                  {pendingTickets.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab("mine")}
                className={`flex-1 basis-0 min-w-0 px-2 py-1 sm:py-1.5 text-[11px] sm:text-sm font-medium rounded-md flex items-center justify-center gap-1.5 transition-all ${activeTab === "mine" ? (darkMode ? "bg-vision-darkGreen text-white shadow-sm" : "bg-white text-[#294b29] shadow-sm") : "text-gray-500 hover:text-gray-700"}`}
              >
                <UserCheck size={16} className="shrink-0" />
                {isAdmin ? null : <span className="hidden sm:inline">Meus</span>}
                <span className={`text-[11px] sm:text-xs px-1.5 py-0.5 rounded-full ${darkMode ? "bg-vision-surface" : "bg-gray-200"}`}>
                  {myTickets.length}
                </span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveTab("all")}
                  className={`flex-1 basis-0 min-w-0 px-2 py-1 sm:py-1.5 text-[11px] sm:text-sm font-medium rounded-md flex items-center justify-center gap-1.5 transition-all ${activeTab === "all" ? (darkMode ? "bg-vision-darkGreen text-white shadow-sm" : "bg-white text-[#294b29] shadow-sm") : "text-gray-500 hover:text-gray-700"}`}
                >
                  <Archive size={16} className="shrink-0" />
                  {/* Admin fica sÃ³ com Ã­cones para caber melhor */}
                  <span className={`text-[11px] sm:text-xs px-1.5 py-0.5 rounded-full ${darkMode ? "bg-vision-surface" : "bg-gray-200"}`}>
                    {allActiveTickets.length}
                  </span>
                </button>
              )}
          </div>
          <div className="relative">
            <Search className={`absolute left-3 top-2.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`} size={18} />
            <input
              type="text"
              placeholder="Buscar ticket ou cliente..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none transition-all ${darkMode ? "bg-vision-darkGreen text-white placeholder-gray-500 border border-transparent focus:border-vision-border" : "bg-gray-100 text-gray-800 placeholder-gray-500 focus:bg-white focus:ring-2 focus:ring-[#294b29]/20"}`}
              value={ticketSearch}
              onChange={e => setTicketSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {ticketsLoading ? (
            <div className="flex flex-col gap-3 p-4">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${darkMode ? "border-vision-border bg-vision-darkGreen/40" : "border-gray-200 bg-white shadow-sm"}`}
                >
                  <div className={`w-10 h-10 rounded-full animate-pulse ${darkMode ? "bg-vision-border" : "bg-gray-200"}`} />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className={`h-3 w-24 rounded animate-pulse ${darkMode ? "bg-vision-border" : "bg-gray-200"}`} />
                    <div className={`h-3 w-16 rounded animate-pulse ${darkMode ? "bg-vision-border/80" : "bg-gray-100"}`} />
                    <div className={`h-3 w-40 rounded animate-pulse ${darkMode ? "bg-vision-border/80" : "bg-gray-100"}`} />
                  </div>
                  <div className={`h-3 w-10 rounded animate-pulse ${darkMode ? "bg-vision-border/80" : "bg-gray-100"}`} />
                </div>
              ))}
            </div>
          ) : (
            <>
              {filteredTickets.map(ticket => (
                <button
                  key={ticket.id}
                  onClick={() => setSelectedTicketId(ticket.id)}
                  className={`w-full p-4 flex items-start gap-3 border-b transition-all text-left relative ${darkMode ? "border-vision-border hover:bg-vision-surface" : "border-gray-100 hover:bg-gray-50"} ${selectedTicketId === ticket.id ? (darkMode ? "bg-vision-surface" : "bg-[#294b29]/5 border-l-4 border-l-[#294b29]") : "border-l-4 border-l-transparent"}`}
                >
                  <div className="relative">
                    {ticket.avatarUrl ? (
                      <img
                        src={ticket.avatarUrl}
                        alt={ticket.customerName}
                        className="w-10 h-10 rounded-full object-cover border border-black/5"
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs ${darkMode ? "bg-vision-darkGreen text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                        {ticket.customerAvatar}
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-white dark:bg-black rounded-full p-0.5">
                      <div className="bg-green-500 text-white rounded-full p-0.5">
                        <MessageCircle size={10} />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <span className={`font-semibold text-sm truncate ${darkMode ? "text-gray-200" : "text-gray-900"}`}>
                        {ticket.customerName}
                      </span>
                      <span className={`text-[10px] ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {activeTab === "pending" ? ticket.waitingSince : ticket.lastUpdate}
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 font-mono ${darkMode ? "text-gray-500" : "text-gray-400"}`}>{ticket.id}</p>
                    <p className={`text-xs mt-1 truncate ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      {ticket.messages[ticket.messages.length - 1]?.text || "Sem mensagens"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {ticket.unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                        {ticket.unreadCount}
                      </span>
                    )}
                    {ticket.status === "pending" && ticket.imobiliariaNome && (
                      <span
                        title={ticket.imobiliariaNome}
                        className="flex items-center gap-1 text-[10px] text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded max-w-[120px] truncate"
                      >
                        <Clock size={10} />
                        <span className="truncate">{ticket.imobiliariaNome}</span>
                      </span>
                    )}
                  </div>
                </button>
              ))}
              {filteredTickets.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-500 opacity-60">
                  <Inbox size={48} className="mb-4" strokeWidth={1} />
                  <p>Nenhum chamado nesta fila.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className={`flex-1 flex-col h-full relative ${selectedTicketId ? "flex" : "hidden md:flex"} ${darkMode ? "bg-[#0b140b]" : "bg-[#e5ddd5]"}`}>
        {selectedTicket ? (
          <>
            <div className={`absolute inset-0 opacity-5 pointer-events-none ${darkMode ? "bg-vision-green" : "bg-black"}`} style={{ backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
            <header className={`min-h-[72px] px-4 md:px-6 flex items-center justify-between shadow-sm z-10 flex-shrink-0 ${darkMode ? "bg-black border-b border-vision-border" : "bg-white border-b border-gray-200"}`}>
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedTicketId(null)} className={`md:hidden p-1 rounded-full ${darkMode ? "text-gray-300 hover:bg-vision-surface" : "text-gray-600 hover:bg-gray-100"}`}>
                  <ArrowLeft size={24} />
                </button>
                {selectedTicket.avatarUrl ? (
                  <button
                    type="button"
                    onClick={() => setAvatarPreviewUrl(selectedTicket.avatarUrl as string)}
                    className="cursor-pointer"
                    aria-label="Ampliar foto do corretor"
                  >
                    <img
                      src={selectedTicket.avatarUrl}
                      alt={selectedTicket.customerName}
                      className="w-10 h-10 rounded-full object-cover border border-black/5"
                    />
                  </button>
                ) : (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${darkMode ? "bg-vision-surface text-gray-300" : "bg-[#294b29] text-white"}`}>
                    {selectedTicket.customerAvatar}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3
                    className={`font-bold text-base md:text-lg truncate ${darkMode ? "text-white" : "text-gray-800"}`}
                    title={selectedTicket.customerName}
                  >
                    {selectedTicket.brokerId ? (
                      <button
                        type="button"
                        onClick={() => openBrokerProfileInNewTab(selectedTicket.brokerId)}
                        className={`hover:underline ${darkMode ? "text-white" : "text-gray-800"}`}
                      >
                        {selectedTicket.customerName}
                      </button>
                    ) : (
                      selectedTicket.customerName
                    )}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {selectedTicket.imobiliariaNome ? (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded w-fit font-bold ${darkMode ? "bg-vision-surface text-green-300 border border-vision-border" : "bg-green-50 text-green-700 border border-green-200"}`}>
                        <AlertCircle size={10} />
                        {selectedTicket.imobiliariaNome}
                      </span>
                    ) : (
                      <button
                        onClick={handleOpenRegisterModal}
                        className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded w-fit font-bold transition-colors shrink-0 ${darkMode ? "bg-yellow-900/30 text-yellow-500 hover:bg-yellow-900/50" : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"}`}
                      >
                        <AlertCircle size={10} />
                        Vincular corretor
                      </button>
                    )}
                    {role === "admin" && selectedTicket.agentName && (
                      <div className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded w-fit font-semibold ${darkMode ? "bg-vision-surface text-blue-200 border border-vision-border" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                        <UserCheck size={10} />
                        Atendente: {selectedTicket.agentName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end relative">
                {/* Mobile toggle */}
                <div className="md:hidden" ref={actionsMenuRef}>
                  <button
                    className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${darkMode ? "border-vision-border text-white hover:bg-vision-surface" : "border-gray-200 text-gray-700 hover:bg-gray-100"}`}
                    onClick={() => setShowActionsMobile(prev => !prev)}
                  >
                    <MoreVertical size={16} />
                    AÃ§Ãµes
                  </button>
                  {showActionsMobile && (
                    <div className={`absolute right-0 top-full mt-2 z-30 flex flex-col gap-2 p-2 rounded-lg shadow-lg border ${darkMode ? "bg-vision-darkGreen border-vision-border" : "bg-white border-gray-200"}`}>
                      {selectedTicket.status === "pending" ? (
                        <>
                          <button
                            onClick={() => { setShowActionsMobile(false); handleQuickClosePending(); }}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg shadow-sm transition-transform active:scale-95"
                          >
                            <XCircle size={16} />
                            Fechar atendimento
                          </button>
                          <button
                            onClick={() => { setShowActionsMobile(false); handleOpenTransferModal(); }}
                            className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border transition-all ${darkMode ? "border-blue-900 text-blue-300 hover:bg-blue-900/30" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                          >
                            <UserPlus size={16} />
                            Transferir
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => { setShowActionsMobile(false); handleOpenTransferModal(); }}
                            className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border transition-all ${darkMode ? "border-blue-900 text-blue-300 hover:bg-blue-900/30" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                          >
                            <UserPlus size={16} />
                            Transferir
                          </button>
                          <button
                            onClick={() => { setShowActionsMobile(false); handleOpenCloseModal(); }}
                            className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border transition-all ${darkMode ? "border-red-900 text-red-400 hover:bg-red-900/30" : "border-red-200 text-red-600 hover:bg-red-50"}`}
                          >
                            <XCircle size={16} />
                            Encerrar
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop actions */}
                <div className="hidden md:flex items-center gap-2 flex-wrap">
                  {selectedTicket.status === "pending" ? (
                    <>
                      <button
                        onClick={handleQuickClosePending}
                        className="flex items-center gap-2 px-3 md:px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs md:text-sm font-bold rounded-lg shadow-md transition-transform active:scale-95"
                      >
                        <XCircle size={16} />
                        <span className="hidden md:inline">Fechar atendimento</span>
                        <span className="md:hidden">Fechar</span>
                      </button>
                      <button
                        onClick={handleOpenTransferModal}
                        className={`flex items-center gap-2 px-3 md:px-4 py-2 text-xs md:text-sm font-bold rounded-lg border transition-all ${darkMode ? "border-blue-900 text-blue-300 hover:bg-blue-900/30" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                      >
                        <UserPlus size={16} />
                        <span>Transferir</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleOpenTransferModal}
                        className={`flex items-center gap-2 px-3 md:px-4 py-2 text-xs md:text-sm font-bold rounded-lg border transition-all ${darkMode ? "border-blue-900 text-blue-300 hover:bg-blue-900/30" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                      >
                        <UserPlus size={16} />
                        <span>Transferir</span>
                      </button>
                      <button
                        onClick={handleOpenCloseModal}
                        className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border transition-all ${darkMode ? "border-red-900 text-red-400 hover:bg-red-900/30" : "border-red-200 text-red-600 hover:bg-red-50"}`}
                      >
                        <XCircle size={16} />
                        <span>Encerrar</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </header>
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 md:p-8 space-y-4 z-10 scrollbar-thin scrollbar-thumb-gray-600">
              {messagesLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-sm ${darkMode ? "bg-black/40 border-vision-border text-gray-200" : "bg-white border-gray-200 text-gray-700"}`}>
                    <span className="w-3 h-3 rounded-full bg-[#294b29] animate-ping" />
                    <span className="text-sm font-medium">Carregando conversas...</span>
                  </div>
                </div>
                ) : (
                  <>
                    {hasMoreMessages && (
                      <div className="flex justify-center mb-4">
                        <button
                          onClick={loadOlderMessages}
                          disabled={loadingMoreMessages}
                          className="px-3 py-1.5 text-xs rounded-md bg-gray-100 dark:bg-vision-surface text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-vision-border hover:bg-gray-200 dark:hover:bg-vision-border transition disabled:opacity-60"
                        >
                          {loadingMoreMessages ? "Carregando..." : "Ver mensagens anteriores"}
                        </button>
                    </div>
                  )}
                  {selectedTicket.messages.map(msg => {
                    if (msg.sender === "system") {
                      return (
                        <div key={msg.id} className="flex justify-center mb-2">
                          <div className={`text-xs px-4 py-1.5 rounded-full shadow-sm ${darkMode ? "bg-vision-surface text-gray-400" : "bg-white text-gray-500"}`}>
                            {msg.text}
                          </div>
                        </div>
                      );
                    }
                    const replyTarget = msg.replyToId
                      ? selectedTicket.messages.find(m => m.whatsappMsgId === msg.replyToId || String(m.id) === String(msg.replyToId))
                      : null;
                    return (
                      <div key={msg.id} className={`flex flex-col ${msg.sender === "me" ? "items-end" : "items-start"}`}>
                        <div
                          className={`max-w-[85%] md:max-w-[60%] rounded-lg px-4 py-3 pr-10 relative shadow-sm text-sm md:text-base ${msg.deleted ? "opacity-70" : ""} ${
                            msg.sender === "me"
                              ? darkMode
                                ? "bg-[#1a301a] border border-[#294b29] text-white rounded-tr-none"
                                : "bg-[#d9fdd3] text-gray-800 rounded-tr-none"
                            : darkMode
                            ? "bg-vision-surface text-white rounded-tl-none"
                            : "bg-white text-gray-800 rounded-tl-none"
                          }`}
                        >
                          <div className="absolute right-1 top-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = menuMessageId === msg.id ? null : msg.id;
                            if (next) {
                              const anchor = toAnchorRect((e.currentTarget as HTMLElement).getBoundingClientRect());
                              setMenuAnchorRect(anchor);
                              updateMenuPosition(anchor, reactionsExpandedFor === msg.id);
                            } else {
                              setMenuAnchorRect(null);
                              setMenuPosition(null);
                            }
                            setMenuMessageId(next);
                            setReactionsExpandedFor(null);
                          }}
                          className={`p-1 rounded ${darkMode ? "text-gray-400 hover:bg-vision-surface" : "text-gray-500 hover:bg-gray-100"}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {menuMessageId === msg.id && (
                            <div
                              className={`fixed w-44 rounded-lg shadow-lg border z-50 ${darkMode ? "bg-black border-vision-border" : "bg-white border-gray-200"}`}
                              style={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
                              onClick={e => e.stopPropagation()}
                            >
                              <button className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-vision-surface" onClick={() => { setReplyTo(msg); setMenuMessageId(null); }}>Responder</button>
                              <div className="grid grid-cols-8 gap-1 px-3 py-2">
                                {(reactionsExpandedFor === msg.id ? ALL_EMOJIS : QUICK_EMOJIS).map(em => (
                                  <button key={em} className="text-lg hover:bg-gray-100 dark:hover:bg-vision-surface rounded" onClick={() => handleReactMessage(msg, em)}>{em}</button>
                                ))}
                              </div>
                              <button
                                className="w-full text-left px-3 py-2 text-[11px] text-gray-500 hover:bg-gray-100 dark:hover:bg-vision-surface"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const expanded = reactionsExpandedFor === msg.id;
                                  const nextExpanded = expanded ? null : msg.id;
                                  setReactionsExpandedFor(nextExpanded);
                                  const fallbackAnchor = (e.currentTarget.parentElement as HTMLElement | null)?.getBoundingClientRect();
                                  const anchorRect = menuAnchorRect || (fallbackAnchor ? toAnchorRect(fallbackAnchor) : null);
                                  if (anchorRect) {
                                    setMenuAnchorRect(anchorRect);
                                    updateMenuPosition(anchorRect, !!nextExpanded);
                                  }
                                }}
                              >
                                {reactionsExpandedFor === msg.id ? "Ver menos emoctions" : "Ver mais emoctions"}
                              </button>
                              {msg.sender === "me" && (
                                <button
                                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-vision-surface ${msg.deleted ? "text-gray-400" : "text-red-600"}`}
                                  disabled={msg.deleted || deletingMessageId === msg.id}
                                  onClick={() => handleDeleteMessage(msg)}
                              >
                                {msg.deleted ? "Apagada" : deletingMessageId === msg.id ? "Apagando..." : "Apagar"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {replyTarget && (
                        <div className={`mb-1 text-xs px-2 py-1 rounded border ${darkMode ? "border-vision-border bg-black/30 text-gray-300" : "border-gray-200 bg-gray-50 text-gray-600"}`}>
                          Respondendo: {replyTarget.text || "(mensagem)"}
                        </div>
                      )}
                      {renderMessageContent(msg)}
                      <div className={`text-[10px] mt-2 flex items-center justify-end gap-2 ${msg.sender === "me" ? (darkMode ? "text-green-400" : "text-gray-500") : "text-gray-400"}`}>
                        {msg.time}
                        {msg.sender === "me" && <span>{msg.status === "read" ? <CheckCheck size={14} className="text-blue-500" /> : <Check size={14} />}</span>}
                      </div>
                  </div>
                  {(!msg.deleted && msg.reactions && msg.reactions.length > 0) && (
                    <div className={`mt-1 flex ${msg.sender === "me" ? "justify-end" : "justify-start"} gap-2`}>
                      {msg.reactions.map((r, idx) => (
                        <div
                          key={`${r.emoji}-${idx}`}
                          className="text-xs inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/70 dark:bg-black/40 border border-white/60 dark:border-black/40 shadow-sm"
                          title={
                            r.actors.length
                              ? `ReaÃ§Ã£o enviada por ${r.actors.join(", ")}`
                              : r.sources.includes("whatsapp")
                              ? "ReaÃ§Ã£o enviada pelo WhatsApp"
                              : r.sources.includes("desk")
                              ? "ReaÃ§Ã£o enviada pelo Desk"
                              : "ReaÃ§Ã£o"
                          }
                        >
                          <span className="text-base leading-none">{r.emoji}</span>
                          {r.count > 1 && <span className="text-[9px] text-gray-500 dark:text-gray-400">x{r.count}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {selectedTicket.status === "open" ? (
              <div className={`${darkMode ? "bg-black border-t border-vision-border" : "bg-white border-t border-gray-200"} p-4 z-10`}>
                {sendError && (
                  <div className="mb-2 text-sm text-red-500">
                    {sendError}
                  </div>
                )}
                {replyTo && (
                  <div ref={replyBarRef} className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${darkMode ? "border-vision-border bg-black/30 text-gray-200" : "border-gray-200 bg-gray-50 text-gray-700"}`}>
                    <div className="text-xs leading-snug">
                      <div className="font-semibold mb-1">Respondendo</div>
                      <div className="line-clamp-2 whitespace-pre-wrap">{replyTo.text || "(mensagem)"}</div>
                    </div>
                    <button
                      type="button"
                      className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
                      onClick={() => setReplyTo(null)}
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="flex flex-col gap-2">
                  {attachment && (
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${darkMode ? "bg-vision-darkGreen border border-vision-border" : "bg-gray-100 border border-gray-200"}`}>
                      {attachment.mime.startsWith("image/") ? (
                        <img src={attachment.previewUrl} alt={attachment.name} className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <Paperclip size={24} />
                      )}
                      <div className="flex-1 truncate text-sm">
                        {attachment.name}
                      </div>
                      <button
                        type="button"
                        className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"}`}
                        onClick={() => {
                          URL.revokeObjectURL(attachment.previewUrl);
                          setAttachment(null);
                        }}
                      >
                        <XCircle size={20} />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"}`}
                      title="Anexar imagem, v?deo ou documento"
                    >
                      <Paperclip size={24} />
                    </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (isRecording) {
                        stopRecording();
                      } else {
                        startRecording();
                      }
                    }}
                    className={`${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"} ${isRecording ? "text-red-500" : ""}`}
                    title={isRecording ? "Parar grava??o" : "Gravar ?udio"}
                  >
                    <Mic size={24} />
                  </button>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={inputText}
                        onChange={e => setInputText(e.target.value)}
                        placeholder="Digite sua resposta..."
                    onPaste={handlePaste}
                    className={`w-full py-3 px-4 rounded-xl outline-none border focus:ring-1 transition-all ${darkMode ? "bg-vision-darkGreen border-vision-border text-white focus:border-green-500" : "bg-white border-gray-300 text-gray-800 focus:border-[#294b29] focus:ring-[#294b29]"}`}
                    autoFocus
                  />
                      <button
                        type="button"
                        onClick={() => setEmojiOpen(v => !v)}
                        className={`absolute right-3 top-3 ${darkMode ? "text-gray-400" : "text-gray-400"}`}>
                        <Smile size={24} />
                      </button>
                      {emojiOpen && (
                        <div className={`absolute right-0 bottom-12 z-20 w-56 rounded-lg shadow-lg border ${darkMode ? "bg-black border-vision-border" : "bg-white border-gray-200"}`}>
                          <div className="grid grid-cols-6 gap-2 p-3 text-lg max-h-64 overflow-y-auto">
                            {ALL_EMOJIS.map(em => (
                              <button
                                key={em}
                                type="button"
                                className="p-2 hover:bg-gray-100 dark:hover:bg-vision-surface rounded text-xl"
                                onClick={() => {
                                  setInputText(prev => prev + em);
                                  setEmojiOpen(false);
                                }}
                              >
                                {em}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={uploading || (!inputText.trim() && !attachment)}
                      className={`p-3 rounded-full transition-all flex items-center justify-center ${
                        uploading || (!inputText.trim() && !attachment)
                          ? darkMode
                            ? "bg-vision-surface text-gray-600"
                            : "bg-gray-200 text-gray-400"
                          : darkMode
                          ? "bg-[#294b29] text-white hover:scale-105"
                          : "bg-[#294b29] text-white hover:scale-105"
                      }`}
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </form>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*,video/*,application/pdf"
                  onChange={e => handleFiles(e.target.files)}
                />
                {uploading && (
                  <div className="mt-2 text-xs text-gray-500">
                    Enviando midia...
                  </div>
                )}
                {isRecording && (
                  <div className="mt-2 text-xs text-red-500 flex items-center gap-2">
                    Gravando ?udio... <button type="button" onClick={stopRecording} className="px-2 py-1 bg-red-600 text-white rounded">Parar</button>
                  </div>
                )}
                {recordingError && (
                  <div className="mt-2 text-xs text-red-500">
                    {recordingError}
                  </div>
                )}
              </div>
            ) : (
              <div className={`${darkMode ? "bg-black border-vision-border" : "bg-gray-50 border-gray-200"} p-6 z-10 flex flex-col items-center justify-center text-center gap-3 border-t`}>
                <AlertCircle className="text-yellow-500" size={32} />
                <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Este chamado est? na <strong>fila de espera</strong>. <br />
                  Voc? precisa assumir o atendimento para responder.
                </p>
                <button onClick={handlePickUpTicket} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md">
                  Assumir Agora
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <div className={`p-6 rounded-full mb-4 ${darkMode ? "bg-vision-surface" : "bg-gray-100"}`}>
              <Archive size={48} strokeWidth={1} />
            </div>
            <h3 className="text-xl font-bold mb-2">Service Desk</h3>
            <p className="max-w-xs text-center text-sm opacity-70">Selecione um chamado da fila de pendentes ou continue seus atendimentos.</p>
          </div>
        )}

                {isRegisterModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${darkMode ? "bg-vision-darkGreen border border-vision-border" : "bg-white"}`}>
              <div className={`p-6 border-b flex-shrink-0 ${darkMode ? "border-vision-border" : "border-gray-100"}`}>
                <h3 className={`text-lg font-bold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-800"}`}>
                  <UserPlus size={20} className="text-blue-500" />
                  Cadastro Rapido de Corretor
                </h3>
                <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Vincule este atendimento a um corretor de um produto e imobiliaria.</p>
              </div>
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Nome do corretor <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={registerData.name}
                    onChange={e => setRegisterData({ ...registerData, name: e.target.value })}
                    placeholder="Nome completo"
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent"}`}
                  />
                </div>
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={registerData.email}
                    onChange={e => setRegisterData({ ...registerData, email: e.target.value })}
                    placeholder="email@dominio.com"
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent"}`}
                  />
                </div>
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Produto vinculado <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={registerData.productId}
                    onChange={e => {
                      const nextProductId = e.target.value;
                      setRegisterData(prev => ({
                        ...prev,
                        productId: nextProductId,
                        imobiliariaId: "",
                        brokerType: "imobiliaria"
                      }));
                      setImobiliariaSearch("");
                      setIsImobiliariaDropdownOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
                  >
                    <option value="">Selecione o produto</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                {registerData.productId && (
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                      Tipo de corretor
                    </label>
                    <select
                      value={registerData.brokerType}
                      onChange={e => {
                        const value = e.target.value as "imobiliaria" | "autonomo" | "cliente";
                        setRegisterData(prev => ({
                          ...prev,
                        brokerType: value,
                          imobiliariaId: value === "autonomo" ? "" : prev.imobiliariaId
                        }));
                        if (value !== "imobiliaria") {
                          setImobiliariaSearch("");
                          setIsImobiliariaDropdownOpen(false);
                        }
                      }}
                      className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
                    >
                      <option value="imobiliaria">Vinculado a imobiliaria</option>
                      <option value="autonomo">Autonomo (sem imobiliaria)</option>
                      <option value="cliente">Cliente</option>
                    </select>
                  </div>
                )}
                <div className="space-y-2 relative" ref={imobiliariaDropdownRef}>
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Imobiliaria <span className="text-red-500">*</span>
                  </label>
                  {(!registerData.productId || registerData.brokerType === "autonomo") && (
                    <p className={`text-xs ${darkMode ? "text-red-400" : "text-red-600"}`}>
                      {registerData.productId
                        ? "Imobiliaria nao se aplica para autonomo."
                        : "Selecione um produto para ver as imobiliarias."}
                    </p>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      value={imobiliariaSearch}
                      onChange={e => {
                        setImobiliariaSearch(e.target.value);
                        setRegisterData(prev => ({ ...prev, imobiliariaId: "" }));
                        if (registerData.productId && registerData.brokerType !== "autonomo") setIsImobiliariaDropdownOpen(true);
                      }}
                      onFocus={() => {
                        if (registerData.productId && registerData.brokerType !== "autonomo") setIsImobiliariaDropdownOpen(true);
                      }}
                      placeholder={registerData.productId ? "Digite para buscar..." : "Selecione um produto primeiro"}
                      disabled={!registerData.productId || registerData.brokerType === "autonomo"}
                      className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all pr-10 ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"} ${(!registerData.productId || registerData.brokerType === "autonomo") ? "opacity-60 cursor-not-allowed" : ""}`}
                    />
                    <div className="absolute right-3 top-3 pointer-events-none opacity-50">
                      <ChevronDown size={16} />
                    </div>
                  </div>
                  {isImobiliariaDropdownOpen && registerData.productId && registerData.brokerType !== "autonomo" && (
                    <ul className={`absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg shadow-xl border ${darkMode ? "bg-vision-surface border-vision-border text-gray-200" : "bg-white border-gray-200 text-gray-800"}`}>
                      {filteredImobiliarias.length > 0 ? (
                        filteredImobiliarias.map(i => (
                          <li
                            key={i.id}
                            onClick={() => {
                              setRegisterData(prev => ({ ...prev, imobiliariaId: String(i.id) }));
                              setImobiliariaSearch(i.name);
                              setIsImobiliariaDropdownOpen(false);
                            }}
                            className={`px-4 py-3 text-sm cursor-pointer transition-colors border-b last:border-0 ${darkMode ? "border-vision-border hover:bg-vision-darkGreen" : "border-gray-50 hover:bg-gray-100"}`}
                          >
                            {i.name}
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-3 text-sm opacity-50 text-center">
                          Nenhuma imobiliaria encontrada.
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
              <div className={`p-4 flex justify-end gap-3 flex-shrink-0 ${darkMode ? "bg-black/20" : "bg-gray-50"}`}>
                <button
                  onClick={() => setIsRegisterModalOpen(false)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:bg-gray-200"}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRegisterSubmit}
                  disabled={!registerData.name.trim() || !registerData.productId || (registerData.brokerType === "imobiliaria" && !registerData.imobiliariaId)}
                  className={`px-6 py-2 rounded-lg font-bold text-white transition-all ${!registerData.name.trim() || !registerData.productId || (registerData.brokerType === "imobiliaria" && !registerData.imobiliariaId) ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  Vincular
                </button>
              </div>
            </div>
          </div>
        )}

                {isTransferModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${darkMode ? "bg-vision-darkGreen border border-vision-border" : "bg-white"}`}>
              <div className={`p-6 border-b flex-shrink-0 ${darkMode ? "border-vision-border" : "border-gray-100"}`}>
                <h3 className={`text-lg font-bold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-800"}`}>
                  <UserPlus size={20} className="text-blue-500" />
                  Transferir atendimento
                </h3>
                <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                  Selecione o atendente que vai assumir este chamado.
                </p>
              </div>
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Atendente <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={transferData.atendenteId}
                    onChange={e => setTransferData({ atendenteId: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
                  >
                    <option value="">Selecione um atendente</option>
                    {transferUsers.map(user => (
                      <option key={user.id} value={user.id}>{user.nome}</option>
                    ))}
                  </select>
                  {!transferUsers.length && (
                    <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Nenhum atendente disponivel.
                    </p>
                  )}
                </div>
                {transferError && (
                  <div className={`text-xs font-semibold ${darkMode ? "text-red-400" : "text-red-600"}`}>
                    {transferError}
                  </div>
                )}
              </div>
              <div className={`p-4 flex justify-end gap-3 flex-shrink-0 ${darkMode ? "bg-black/20" : "bg-gray-50"}`}>
                <button
                  onClick={() => setIsTransferModalOpen(false)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:bg-gray-200"}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleTransferSubmit}
                  disabled={!transferData.atendenteId.trim() || transferLoading}
                  className={`px-6 py-2 rounded-lg font-bold text-white transition-all ${!transferData.atendenteId.trim() || transferLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {transferLoading ? "Transferindo..." : "Transferir"}
                </button>
              </div>
            </div>
          </div>
        )}

                {isCloseModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`${darkMode ? "bg-vision-darkGreen border border-vision-border" : "bg-white"} w-full max-w-md max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col`}>
              <div className={`p-6 border-b flex-shrink-0 ${darkMode ? "border-vision-border" : "border-gray-100"}`}>
                <h3 className={`text-lg font-bold flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-800"}`}>
                  <CheckSquare size={20} className="text-green-500" />
                  Finalizar Atendimento
                </h3>
              </div>
              <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Produto vinculado <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={closeData.productId}
                    onChange={e => setCloseData({ ...closeData, productId: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
                  >
                    <option value="">Selecione o produto</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {!selectedTicket?.imobiliariaId && (
                    <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Vincule uma imobiliaria para sugerir os produtos corretos.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Categoria do chamado <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Tag size={16} className={`absolute left-3 top-3.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
                    <select
                      value={closeData.category}
                      onChange={e => setCloseData({ ...closeData, category: e.target.value })}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent text-gray-800"}`}
                    >
                      <option value="">Selecione uma categoria...</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Motivo / Resolução
                  </label>
                  <textarea
                    rows={4}
                    value={closeData.reason}
                    onChange={e => setCloseData({ ...closeData, reason: e.target.value })}
                    placeholder="Descreva brevemente o que foi acordado..."
                    className={`w-full px-4 py-2.5 rounded-lg border outline-none focus:ring-2 transition-all resize-none ${darkMode ? "bg-vision-surface border-vision-border focus:ring-green-900 text-white" : "bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent"}`}
                  />
                </div>
              </div>
              <div className={`p-4 flex justify-end gap-3 flex-shrink-0 ${darkMode ? "bg-black/20" : "bg-gray-50"}`}>
                <button
                  onClick={() => setIsCloseModalOpen(false)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:bg-gray-200"}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleStatusUpdate("encerrado")}
                  disabled={!closeData.category || !closeData.productId}
                  className={`px-6 py-2 rounded-lg font-bold text-white transition-all ${!closeData.category || !closeData.productId ? "bg-gray-400 cursor-not-allowed" : darkMode ? "bg-[#294b29] hover:bg-green-800" : "bg-[#294b29] hover:bg-[#1a301a]"}`}
                >
                  Concluir e Arquivar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export { SupportPage };






